import { useEffect, useMemo, useRef, useState } from 'react';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import FormattedDateInput from '../../components/ui/FormattedDateInput';
import FormattedDateRangeInput from '../../components/ui/FormattedDateRangeInput';
import WizardSelect from '../../components/ui/WizardSelect';
import {
  ReviewChips,
  ReviewField,
  ReviewGrid,
  ReviewSection,
  ReviewSubBlock,
} from '../../components/criminal/CriminalReviewUi';
import {
  getCivilLitigationRequestViewData,
  saveCivilLitigationRequest,
  type CivilLitigationRequestViewData,
} from '../../services/civilLitigationCaseRequests';
import type { CivilLitigationWizardStep } from '../../pages/wizard/civilLitigation/civilLitigationConstants';
import {
  CIVIL_LITIGATION_PARTY_BODY_TYPE_LABELS,
  isOtherCourtType,
} from '../../pages/wizard/civilLitigation/civilLitigationConstants';
import type { CivilLitigationWizardDraft } from '../../types/civilLitigationCaseRequest';
import type { CriminalSaveStep4 } from '../../types/criminalApi';
import type { CriminalWizardDraft } from '../../types/criminalCaseRequest';
import {
  isCaActionsAgainstStateSubCategory,
  isCivilLitigationOriginalCourtsCategory,
  isHighUrgencyLevel,
  isOtherCorporateInstitute,
  isOtherNextStep,
  requiresCourtType,
} from '../../pages/wizard/civilLitigation/civilLitigationHelpers';
import {
  minDeadlineDateForCivilLitigation,
  validateCivilLitigationStep,
} from '../../pages/wizard/civilLitigation/civilLitigationStepValidation';
import { useCivilLitigationLookups } from '../../pages/wizard/civilLitigation/useCivilLitigationLookups';
import {
  deliveryMethodDisplay,
  submitterFromApiStep,
} from '../view-request/viewRequestHelpers';
import {
  CONTACT_NUMBER_LENGTH,
  sliceMax,
} from '../../utils/caseFieldValidation';
import { formatDisplayDateTime } from '../../utils/dateFormat';
import { setFlashToast } from '../../utils/flashToast';
import { focusFirstInvalid } from '../../utils/requiredValidation';
import { FieldError, inpClass } from './criminalFieldUi';
import {
  confirmCaseUpdateCancel,
  formatSubmittedAtLabel,
  isCaseRequestUuid,
  mergeSavedStepIntoInitialBaseline,
  toSelectOpts,
} from './taskCaseUpdateHelpers';
import TaskCaseUpdateSectionActions from './TaskCaseUpdateSectionActions';

type Props = {
  caseRequestId: string;
  recentSubmittedAt: string;
  readOnly?: boolean;
  viewData?: CivilLitigationRequestViewData | null;
  viewDataLoading?: boolean;
  onCancelConfirmed?: () => void;
};

const MAX_DESC = 2000;
const MAX_REF = 255;
const MAX_TEXT = 2000;
const MAX_CONTACT = CONTACT_NUMBER_LENGTH;

function cloneDraft(d: CivilLitigationWizardDraft): CivilLitigationWizardDraft {
  return {
    ...d,
    applicableAreas: [...d.applicableAreas],
    applicableAreaIds: [...d.applicableAreaIds],
    publicCorporationNames: [...d.publicCorporationNames],
    deliveryMethodIds: [...d.deliveryMethodIds],
    deliveryMethods: [...d.deliveryMethods],
    documents: d.documents.map((doc) => ({
      ...doc,
      files: doc.files.map((f) => ({ ...f })),
    })),
  };
}

function draftHasUnsavedChanges(
  current: CivilLitigationWizardDraft,
  saved: CivilLitigationWizardDraft,
): boolean {
  return JSON.stringify(current) !== JSON.stringify(saved);
}

function pickStep1Snapshot(d: CivilLitigationWizardDraft) {
  return {
    description: d.description,
    provinceId: d.provinceId,
    districtId: d.districtId,
    yourReferenceNumber: d.yourReferenceNumber,
    otherExternalReferences: d.otherExternalReferences,
    hasActionInstituted: d.hasActionInstituted,
    courtTypeId: d.courtTypeId,
    courtTypeOther: d.courtTypeOther,
    courtId: d.courtId,
    caseNumber: d.caseNumber,
    hasConnectedMatters: d.hasConnectedMatters,
    connectedCaseNumber: d.connectedCaseNumber,
    connectedAgdFileRefAvailable: d.connectedAgdFileRefAvailable,
    connectedAgdFileRef: d.connectedAgdFileRef,
  };
}

function pickStep2Snapshot(d: CivilLitigationWizardDraft) {
  return {
    plaintiffName: d.plaintiffName,
    defendantName: d.defendantName,
    involvesGovernmentBody: d.involvesGovernmentBody,
    involvesPublicCorporation: d.involvesPublicCorporation,
    publicCorporationNames: d.publicCorporationNames,
    otherInvolvedInstitutions: d.otherInvolvedInstitutions,
    legalOfficerName: d.legalOfficerName,
    legalOfficerContact: d.legalOfficerContact,
  };
}

function pickStep3Snapshot(d: CivilLitigationWizardDraft) {
  return {
    subjectOfDispute: d.subjectOfDispute,
    incidentDateFrom: d.incidentDateFrom,
    incidentDateTo: d.incidentDateTo,
    nextStepOfCaseId: d.nextStepOfCaseId,
    nextStepOfCaseOther: d.nextStepOfCaseOther,
    nextCaseDate: d.nextCaseDate,
    urgencyLevelId: d.urgencyLevelId,
    deadline: d.deadline,
    urgencyJustification: d.urgencyJustification,
  };
}

function stepHasData(step: 1 | 2 | 3, d: CivilLitigationWizardDraft): boolean {
  if (step === 1) {
    return !!(
      d.description.trim() ||
      d.yourReferenceNumber.trim() ||
      d.provinceId.trim() ||
      d.caseNumber.trim() ||
      d.hasActionInstituted
    );
  }
  if (step === 2) {
    return !!(
      d.plaintiffName.trim() ||
      d.defendantName.trim() ||
      d.legalOfficerName.trim() ||
      d.legalOfficerContact.trim()
    );
  }
  return !!(
    d.subjectOfDispute.trim() ||
    d.urgencyLevelId.trim() ||
    d.deadline.trim() ||
    d.urgencyJustification.trim()
  );
}

function canSaveStep(
  step: 1 | 2 | 3,
  draft: CivilLitigationWizardDraft | null,
  initial: CivilLitigationWizardDraft | null,
): boolean {
  if (!draft || !initial) return false;
  const pick =
    step === 1 ? pickStep1Snapshot : step === 2 ? pickStep2Snapshot : pickStep3Snapshot;
  const changed = JSON.stringify(pick(draft)) !== JSON.stringify(pick(initial));
  return (stepHasData(step, draft) || stepHasData(step, initial)) && changed;
}

export default function TaskCaseCivilLitigationUpdateTab({
  caseRequestId,
  recentSubmittedAt,
  readOnly = false,
  viewData,
  viewDataLoading = false,
  onCancelConfirmed,
}: Props) {
  const [draft, setDraft] = useState<CivilLitigationWizardDraft | null>(null);
  const [initial, setInitial] = useState<CivilLitigationWizardDraft | null>(null);
  const [step4, setStep4] = useState<CivilLitigationRequestViewData['step4']>();
  const [submittedLabel, setSubmittedLabel] = useState(recentSubmittedAt);
  const [loadedForKey, setLoadedForKey] = useState<string | null>(null);
  const [loadError, setLoadError] = useState('');
  const [savingStep, setSavingStep] = useState<CivilLitigationWizardStep | null>(null);
  const saveInFlightRef = useRef(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const isSaving = savingStep !== null;

  const lookups = useCivilLitigationLookups(
    'review',
    draft?.provinceId ?? '',
    draft?.districtId ?? '',
    draft?.courtTypeId ?? '',
  );
  const minDeadlineDate = useMemo(() => minDeadlineDateForCivilLitigation(), []);
  const useSharedViewData = viewData !== undefined || viewDataLoading;
  const loadKey = `${caseRequestId}|${recentSubmittedAt}|${viewData?.submittedAt ?? ''}|${viewData?.draft.draftRequestId ?? ''}`;
  const loading = useSharedViewData
    ? viewDataLoading || loadedForKey !== loadKey
    : loadedForKey !== loadKey;

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (!isCaseRequestUuid(caseRequestId)) {
        if (cancelled) return;
        setDraft(null);
        setInitial(null);
        setLoadError('Case request id is missing or invalid.');
        setLoadedForKey(loadKey);
        return;
      }

      if (useSharedViewData) {
        if (viewDataLoading) return;
        if (!viewData?.draft) {
          if (cancelled) return;
          setDraft(null);
          setInitial(null);
          setStep4(undefined);
          setLoadError('Could not load case update data.');
          setLoadedForKey(loadKey);
          return;
        }
        if (cancelled) return;
        const loaded = cloneDraft(viewData.draft);
        setDraft(loaded);
        setInitial(cloneDraft(loaded));
        setStep4(viewData.step4);
        setSubmittedLabel(formatSubmittedAtLabel(viewData.submittedAt, recentSubmittedAt));
        setLoadError('');
        setLoadedForKey(loadKey);
        return;
      }

      const res = await getCivilLitigationRequestViewData(caseRequestId);
      if (cancelled) return;
      if (!res.success || !res.data?.draft) {
        setDraft(null);
        setInitial(null);
        setStep4(undefined);
        setLoadError((!res.success ? res.message : undefined) ?? 'Could not load case update data.');
        setLoadedForKey(loadKey);
        return;
      }
      const loaded = cloneDraft(res.data.draft);
      setDraft(loaded);
      setInitial(cloneDraft(loaded));
      setStep4(res.data.step4);
      setSubmittedLabel(formatSubmittedAtLabel(res.data.submittedAt, recentSubmittedAt));
      setLoadError('');
      setLoadedForKey(loadKey);
    })();
    return () => {
      cancelled = true;
    };
  }, [caseRequestId, loadKey, recentSubmittedAt, useSharedViewData, viewData, viewDataLoading]);

  const canSaveStep1 = useMemo(() => canSaveStep(1, draft, initial), [draft, initial]);
  const canSaveStep2 = useMemo(() => canSaveStep(2, draft, initial), [draft, initial]);
  const canSaveStep3 = useMemo(() => canSaveStep(3, draft, initial), [draft, initial]);
  const clearErr = (key: string) => setErrors((e) => ({ ...e, [key]: '' }));

  const saveSection = async (step: 1 | 2 | 3) => {
    if (!draft || readOnly || saveInFlightRef.current) return;
    const result = validateCivilLitigationStep(
      step,
      draft,
      [],
      lookups.corporateInstitutes,
      [],
    );
    setErrors(result.errors);
    if (!result.valid) {
      focusFirstInvalid(result.specs, result.errors);
      return;
    }

    saveInFlightRef.current = true;
    setSavingStep(step);
    try {
      const r = await saveCivilLitigationRequest(draft, {
        isDraft: false,
        confirmedCorrect: true,
        currentStep: step,
        isUpdateAlreadySubmitted: true,
      });
      if (!r.success) {
        setFlashToast({ message: r.message ?? 'Failed to save case update.', variant: 'error' });
        return;
      }
      const next = {
        ...draft,
        draftRequestId: r.data.requestId || draft.draftRequestId,
        referenceNo: r.data.fileReferenceNumber ?? draft.referenceNo,
      };
      setDraft(next);
      setInitial((prev) => {
        if (!prev) return cloneDraft(next);
        const pick =
          step === 1 ? pickStep1Snapshot : step === 2 ? pickStep2Snapshot : pickStep3Snapshot;
        return mergeSavedStepIntoInitialBaseline(prev, next, pick, cloneDraft);
      });
      setErrors({});
      setFlashToast({ message: 'Case update saved successfully.', variant: 'success' });
    } finally {
      saveInFlightRef.current = false;
      setSavingStep(null);
    }
  };

  const resetSection = () => {
    if (initial) {
      setDraft(cloneDraft(initial));
      setErrors({});
    }
  };

  const beginCancel = () => {
    if (isSaving || !draft || !initial) return;
    if (draftHasUnsavedChanges(draft, initial)) {
      setCancelConfirmOpen(true);
      return;
    }
    resetSection();
  };

  if (loading) return <p className="tcd-update-ts">Loading case update…</p>;
  if (!draft) {
    return (
      <div className="tcd-case-update-error" role="alert">
        <p className="tcd-update-ts" style={{ color: 'var(--red)', margin: 0 }}>
          {loadError || 'Could not load case update data.'}
        </p>
      </div>
    );
  }

  const submitter = submitterFromApiStep(step4);
  const deliveryLabel = deliveryMethodDisplay(
    draft as unknown as CriminalWizardDraft,
    lookups.deliveryMethods,
    step4 as CriminalSaveStep4 | undefined,
  );
  const instituteLabel =
    draft.instituteType === 'corporation'
      ? isOtherCorporateInstitute(draft.corporateInstituteId, draft.corporateInstituteName)
        ? [draft.corporateInstituteOther, draft.corporateInstituteName].filter(Boolean).join(' · ') ||
          'Corporation'
        : draft.corporateInstituteName || 'Corporation'
      : draft.instituteType === 'government'
        ? 'Government Department'
        : '—';

  const isOriginalCourts = isCivilLitigationOriginalCourtsCategory(
    draft.fileCategory,
    draft.subFileCategoryName,
  );
  const isCaSubCategory = isCaActionsAgainstStateSubCategory(
    draft.subFileCategoryId,
    [],
    draft.subFileCategoryName,
  );
  const showCourtType = requiresCourtType(
    draft.hasActionInstituted,
    draft.subFileCategoryId,
    [],
    draft.subFileCategoryName,
  );
  const showCourtName = draft.hasActionInstituted === 'yes';
  const showCourtOther = showCourtType && isOtherCourtType(draft.courtTypeId, draft.courtTypeName);
  const showNextStepOther = isOtherNextStep(draft.nextStepOfCaseName, draft.nextStepOfCaseId);
  const urgencyJustificationRequired = isHighUrgencyLevel(draft.urgencyLevelName);
  const partyBodyTypeLabel =
    draft.involvesPublicCorporation === 'yes'
      ? CIVIL_LITIGATION_PARTY_BODY_TYPE_LABELS.incorporated
      : draft.involvesGovernmentBody === 'yes'
        ? CIVIL_LITIGATION_PARTY_BODY_TYPE_LABELS.government
        : '—';

  return (
    <>
      <ConfirmDialog
        isOpen={cancelConfirmOpen}
        title="Cancel Changes"
        message="Are you sure you want to cancel? Any unsaved changes will be lost."
        onClose={() => setCancelConfirmOpen(false)}
        onConfirm={() => {
          confirmCaseUpdateCancel(resetSection, () => setCancelConfirmOpen(false), onCancelConfirmed);
        }}
      />

      <div className="tcd-case-update">
        <p className="tcd-update-ts">
          Request Submitted at:{' '}
          <strong>{submittedLabel || formatDisplayDateTime(recentSubmittedAt) || '—'}</strong>
        </p>

        <ReviewSection step="Step 01" title="Case Overview">
          <ReviewSubBlock title="Case Categorization">
            <ReviewGrid cols={4}>
              <ReviewField label="File Category" value={draft.fileCategory} />
              <ReviewField label="Sub File Category" value={draft.subFileCategoryName} />
              <ReviewField label="Institute Type" value={instituteLabel} />
            </ReviewGrid>
            <ReviewChips items={draft.applicableAreas} />
          </ReviewSubBlock>

          <ReviewSubBlock title="Case Details">
            <div className="ccr-fg" style={{ marginBottom: 14 }}>
              <label>
                Description <span className="req">*</span>
              </label>
              <textarea
                id="clit-description"
                className={inpClass(Boolean(errors.description))}
                rows={4}
                maxLength={MAX_DESC}
                value={draft.description}
                readOnly={readOnly}
                disabled={readOnly}
                onChange={(e) => {
                  setDraft((d) => (d ? { ...d, description: sliceMax(e.target.value, MAX_DESC) } : d));
                  clearErr('description');
                }}
              />
              <FieldError message={errors.description} />
            </div>

            <div className="ccr-fr c2">
              <div className="ccr-fg">
                <label>
                  Province <span className="req">*</span>
                </label>
                <WizardSelect
                  id="clit-province"
                  hasError={Boolean(errors.provinceId)}
                  value={draft.provinceId}
                  disabled={readOnly}
                  placeholder="Select Province"
                  options={toSelectOpts(lookups.provinces)}
                  onChange={(id) => {
                    const name = lookups.provinces.find((p) => p.id === id)?.name ?? '';
                    setDraft((d) =>
                      d
                        ? {
                            ...d,
                            provinceId: id,
                            provinceName: name,
                            districtId: '',
                            districtName: '',
                            courtId: '',
                            courtName: '',
                          }
                        : d,
                    );
                    clearErr('provinceId');
                  }}
                />
                <FieldError message={errors.provinceId} />
              </div>
              <div className="ccr-fg">
                <label>District</label>
                <WizardSelect
                  id="clit-district"
                  value={draft.districtId}
                  disabled={readOnly || !draft.provinceId}
                  placeholder={draft.provinceId ? 'Select District' : 'Select province first'}
                  options={toSelectOpts(lookups.districts)}
                  onChange={(id) => {
                    const name = lookups.districts.find((x) => x.id === id)?.name ?? '';
                    setDraft((d) =>
                      d
                        ? { ...d, districtId: id, districtName: name, courtId: '', courtName: '' }
                        : d,
                    );
                  }}
                />
              </div>
              <div className="ccr-fg">
                <label>
                  Your Reference Number (External) <span className="req">*</span>
                </label>
                <input
                  id="clit-yourRef"
                  className={inpClass(Boolean(errors.yourReferenceNumber))}
                  value={draft.yourReferenceNumber}
                  readOnly={readOnly}
                  disabled={readOnly}
                  maxLength={MAX_REF}
                  onChange={(e) => {
                    setDraft((d) =>
                      d ? { ...d, yourReferenceNumber: sliceMax(e.target.value, MAX_REF) } : d,
                    );
                    clearErr('yourReferenceNumber');
                  }}
                />
                <FieldError message={errors.yourReferenceNumber} />
              </div>
              <div className="ccr-fg">
                <label>Other External Reference Number(s)</label>
                <input
                  className={inpClass(Boolean(errors.otherExternalReferences))}
                  value={draft.otherExternalReferences}
                  readOnly={readOnly}
                  disabled={readOnly}
                  maxLength={MAX_REF}
                  onChange={(e) =>
                    setDraft((d) =>
                      d ? { ...d, otherExternalReferences: sliceMax(e.target.value, MAX_REF) } : d,
                    )
                  }
                />
                <FieldError message={errors.otherExternalReferences} />
              </div>
              <div className="ccr-fg" id="clit-actionInstituted">
                <label>
                  Has action been instituted? <span className="req">*</span>
                </label>
                <div className="ccr-radio-row">
                  <label className="ccr-radio">
                    <input
                      type="radio"
                      name="clit-actionInstituted"
                      checked={draft.hasActionInstituted === 'yes'}
                      disabled={readOnly}
                      onChange={() => {
                        setDraft((d) => (d ? { ...d, hasActionInstituted: 'yes' } : d));
                        clearErr('hasActionInstituted');
                      }}
                    />
                    Yes
                  </label>
                  <label
                    className="ccr-radio"
                    style={isCaSubCategory ? { cursor: 'not-allowed', opacity: 0.5 } : undefined}
                  >
                    <input
                      type="radio"
                      name="clit-actionInstituted"
                      checked={draft.hasActionInstituted === 'no'}
                      disabled={readOnly || isCaSubCategory}
                      onChange={() => {
                        setDraft((d) => {
                          if (!d) return d;
                          return {
                            ...d,
                            hasActionInstituted: 'no',
                            courtId: '',
                            courtName: '',
                            ...(isCaSubCategory
                              ? {}
                              : { courtTypeId: '', courtTypeName: '', courtTypeOther: '' }),
                          };
                        });
                        clearErr('hasActionInstituted');
                        clearErr('courtTypeId');
                      }}
                    />
                    No
                  </label>
                </div>
                <FieldError message={errors.hasActionInstituted} />
              </div>
              {showCourtType ? (
                <div className="ccr-fg">
                  <label>
                    Court Type <span className="req">*</span>
                  </label>
                  <WizardSelect
                    id="clit-courtType"
                    hasError={Boolean(errors.courtTypeId)}
                    value={draft.courtTypeId}
                    disabled={readOnly}
                    placeholder="Select Court Type"
                    options={toSelectOpts(lookups.courtTypes)}
                    onChange={(id) => {
                      const name = lookups.courtTypes.find((x) => x.id === id)?.name ?? '';
                      setDraft((d) =>
                        d
                          ? {
                              ...d,
                              courtTypeId: id,
                              courtTypeName: name,
                              courtTypeOther: '',
                              courtId: '',
                              courtName: '',
                            }
                          : d,
                      );
                      clearErr('courtTypeId');
                    }}
                  />
                  <FieldError message={errors.courtTypeId} />
                </div>
              ) : null}
              {showCourtName ? (
                <div className="ccr-fg">
                  <label>Court Name</label>
                  <WizardSelect
                    id="clit-courtName"
                    value={draft.courtId}
                    disabled={readOnly || !draft.courtTypeId || lookups.courtsLoading}
                    placeholder={lookups.courtsLoading ? 'Loading courts…' : 'Select Court Name'}
                    options={toSelectOpts(lookups.courts)}
                    onChange={(id) => {
                      const name = lookups.courts.find((x) => x.id === id)?.name ?? '';
                      setDraft((d) => (d ? { ...d, courtId: id, courtName: name } : d));
                    }}
                  />
                </div>
              ) : null}
              {showCourtOther ? (
                <div className="ccr-fg">
                  <label>Court Type (Other)</label>
                  <input
                    id="clit-courtTypeOther"
                    className={inpClass(Boolean(errors.courtTypeOther))}
                    value={draft.courtTypeOther}
                    readOnly={readOnly}
                    disabled={readOnly}
                    maxLength={MAX_REF}
                    onChange={(e) => {
                      setDraft((d) =>
                        d ? { ...d, courtTypeOther: sliceMax(e.target.value, MAX_REF) } : d,
                      );
                      clearErr('courtTypeOther');
                    }}
                  />
                  <FieldError message={errors.courtTypeOther} />
                </div>
              ) : null}
              <div className="ccr-fg">
                <label>
                  Case Number <span className="req">*</span>
                </label>
                <input
                  id="clit-caseNumber"
                  className={inpClass(Boolean(errors.caseNumber))}
                  value={draft.caseNumber}
                  readOnly={readOnly}
                  disabled={readOnly}
                  maxLength={MAX_REF}
                  onChange={(e) => {
                    setDraft((d) => (d ? { ...d, caseNumber: sliceMax(e.target.value, MAX_REF) } : d));
                    clearErr('caseNumber');
                  }}
                />
                <FieldError message={errors.caseNumber} />
              </div>
              <div className="ccr-fg" id="clit-connected">
                <label>
                  Are there any connected matters (Cases)? <span className="req">*</span>
                </label>
                <div className="ccr-radio-row">
                  {(['yes', 'no'] as const).map((v) => (
                    <label key={v} className="ccr-radio">
                      <input
                        type="radio"
                        name="clit-connected"
                        checked={draft.hasConnectedMatters === v}
                        disabled={readOnly}
                        onChange={() => {
                          setDraft((d) =>
                            d
                              ? {
                                  ...d,
                                  hasConnectedMatters: v,
                                  connectedCaseNumber: v === 'yes' ? d.connectedCaseNumber : '',
                                  connectedAgdFileRefAvailable:
                                    v === 'yes' ? d.connectedAgdFileRefAvailable : false,
                                  connectedAgdFileRef: v === 'yes' ? d.connectedAgdFileRef : '',
                                }
                              : d,
                          );
                          clearErr('hasConnectedMatters');
                        }}
                      />
                      {v === 'yes' ? 'Yes' : 'No'}
                    </label>
                  ))}
                </div>
                <FieldError message={errors.hasConnectedMatters} />
              </div>
              {draft.hasConnectedMatters === 'yes' ? (
                <>
                  <div className="ccr-fg">
                    <label>
                      Connected Case Number <span className="req">*</span>
                    </label>
                    <input
                      id="clit-connectedCase"
                      className={inpClass(Boolean(errors.connectedCaseNumber))}
                      value={draft.connectedCaseNumber}
                      readOnly={readOnly}
                      disabled={readOnly}
                      maxLength={MAX_REF}
                      onChange={(e) => {
                        setDraft((d) =>
                          d ? { ...d, connectedCaseNumber: sliceMax(e.target.value, MAX_REF) } : d,
                        );
                        clearErr('connectedCaseNumber');
                      }}
                    />
                    <FieldError message={errors.connectedCaseNumber} />
                  </div>
                  <div className="ccr-fg">
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      Connected AGD File Reference Number
                      {draft.connectedAgdFileRefAvailable ? <span className="req">*</span> : null}
                      {!readOnly ? (
                        <>
                          <input
                            type="checkbox"
                            style={{ width: 15, height: 15, accentColor: '#000', cursor: 'pointer' }}
                            checked={draft.connectedAgdFileRefAvailable}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setDraft((d) =>
                                d
                                  ? {
                                      ...d,
                                      connectedAgdFileRefAvailable: checked,
                                      connectedAgdFileRef: checked ? d.connectedAgdFileRef : '',
                                    }
                                  : d,
                              );
                              clearErr('connectedAgdFileRef');
                            }}
                          />
                          <span>Available</span>
                        </>
                      ) : null}
                    </label>
                    <input
                      id="clit-connectedAgd"
                      className={inpClass(Boolean(errors.connectedAgdFileRef))}
                      value={draft.connectedAgdFileRef}
                      readOnly={readOnly}
                      disabled={readOnly || !draft.connectedAgdFileRefAvailable}
                      maxLength={MAX_REF}
                      onChange={(e) => {
                        setDraft((d) =>
                          d ? { ...d, connectedAgdFileRef: sliceMax(e.target.value, MAX_REF) } : d,
                        );
                        clearErr('connectedAgdFileRef');
                      }}
                    />
                    <FieldError message={errors.connectedAgdFileRef} />
                  </div>
                </>
              ) : null}
            </div>
          </ReviewSubBlock>

          <TaskCaseUpdateSectionActions
            canSave={canSaveStep1}
            saving={savingStep === 1}
            readOnly={readOnly}
            onSave={() => void saveSection(1)}
            onCancel={beginCancel}
          />
        </ReviewSection>

        <ReviewSection step="Step 02" title="Party Details">
          <ReviewSubBlock title="Party Details">
            <div className="ccr-fr c2">
              <div className="ccr-fg">
                <label>
                  Plaintiff / Applicant Name <span className="req">*</span>
                </label>
                <input
                  id="clit-plaintiff"
                  className={inpClass(Boolean(errors.plaintiffName))}
                  value={draft.plaintiffName}
                  readOnly={readOnly}
                  disabled={readOnly}
                  maxLength={MAX_REF}
                  onChange={(e) => {
                    setDraft((d) =>
                      d ? { ...d, plaintiffName: sliceMax(e.target.value, MAX_REF) } : d,
                    );
                    clearErr('plaintiffName');
                  }}
                />
                <FieldError message={errors.plaintiffName} />
              </div>
              <div className="ccr-fg">
                <label>
                  Defendant / Respondent Name <span className="req">*</span>
                </label>
                <input
                  id="clit-defendant"
                  className={inpClass(Boolean(errors.defendantName))}
                  value={draft.defendantName}
                  readOnly={readOnly}
                  disabled={readOnly}
                  maxLength={MAX_REF}
                  onChange={(e) => {
                    setDraft((d) =>
                      d ? { ...d, defendantName: sliceMax(e.target.value, MAX_REF) } : d,
                    );
                    clearErr('defendantName');
                  }}
                />
                <FieldError message={errors.defendantName} />
              </div>
              <div className="ccr-fg" id="clit-partyBodyType" style={{ gridColumn: '1 / -1' }}>
                <label>
                  Select one of the following <span className="req">*</span>
                </label>
                {readOnly ? (
                  <p style={{ margin: 0 }}>{partyBodyTypeLabel}</p>
                ) : (
                  <div className="ccr-radio-row">
                    <label className="ccr-radio">
                      <input
                        type="radio"
                        name="clit-partyBodyType"
                        checked={draft.involvesGovernmentBody === 'yes'}
                        onChange={() => {
                          setDraft((d) =>
                            d
                              ? {
                                  ...d,
                                  involvesGovernmentBody: 'yes',
                                  involvesPublicCorporation: 'no',
                                  publicCorporationNames: [''],
                                }
                              : d,
                          );
                          clearErr('partyBodyType');
                          clearErr('publicCorporationNames');
                        }}
                      />
                      {CIVIL_LITIGATION_PARTY_BODY_TYPE_LABELS.government}
                    </label>
                    <label className="ccr-radio">
                      <input
                        type="radio"
                        name="clit-partyBodyType"
                        checked={draft.involvesPublicCorporation === 'yes'}
                        onChange={() => {
                          setDraft((d) =>
                            d
                              ? {
                                  ...d,
                                  involvesGovernmentBody: 'no',
                                  involvesPublicCorporation: 'yes',
                                  publicCorporationNames: d.publicCorporationNames.length
                                    ? d.publicCorporationNames
                                    : [''],
                                }
                              : d,
                          );
                          clearErr('partyBodyType');
                        }}
                      />
                      {CIVIL_LITIGATION_PARTY_BODY_TYPE_LABELS.incorporated}
                    </label>
                  </div>
                )}
                <FieldError message={errors.partyBodyType} />
              </div>
              {!isOriginalCourts && draft.involvesPublicCorporation === 'yes' ? (
                <div className="ccr-fg" style={{ gridColumn: '1 / -1' }}>
                  {draft.publicCorporationNames.map((name, index) => (
                    <div key={index} className="ccr-fg" style={{ marginBottom: 10 }}>
                      <label>
                        Public Corporation Name(s)
                        {draft.publicCorporationNames.length > 1 ? ` #${index + 1}` : ''}
                      </label>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                        <input
                          className={inpClass(
                            Boolean(errors[`publicCorp-${index}`] || errors.publicCorporationNames),
                          )}
                          value={name}
                          readOnly={readOnly}
                          disabled={readOnly}
                          maxLength={MAX_REF}
                          onChange={(e) => {
                            const value = sliceMax(e.target.value, MAX_REF);
                            setDraft((d) =>
                              d
                                ? {
                                    ...d,
                                    publicCorporationNames: d.publicCorporationNames.map((n, i) =>
                                      i === index ? value : n,
                                    ),
                                  }
                                : d,
                            );
                            clearErr(`publicCorp-${index}`);
                            clearErr('publicCorporationNames');
                          }}
                        />
                        {!readOnly && draft.publicCorporationNames.length > 1 ? (
                          <button
                            className="ccr-btn-ol"
                            type="button"
                            onClick={() =>
                              setDraft((d) =>
                                d
                                  ? {
                                      ...d,
                                      publicCorporationNames: d.publicCorporationNames.filter(
                                        (_, i) => i !== index,
                                      ),
                                    }
                                  : d,
                              )
                            }
                          >
                            Remove
                          </button>
                        ) : null}
                      </div>
                      <FieldError
                        message={errors[`publicCorp-${index}`] ?? errors.publicCorporationNames}
                      />
                    </div>
                  ))}
                  {!readOnly ? (
                    <button
                      className="ccr-btn-ol"
                      type="button"
                      onClick={() =>
                        setDraft((d) =>
                          d ? { ...d, publicCorporationNames: [...d.publicCorporationNames, ''] } : d,
                        )
                      }
                    >
                      Add Corporation
                    </button>
                  ) : null}
                </div>
              ) : null}
              <div className="ccr-fg" style={{ gridColumn: '1 / -1' }}>
                <label>Other Involved Institutions</label>
                <textarea
                  className={inpClass(Boolean(errors.otherInvolvedInstitutions))}
                  rows={3}
                  maxLength={MAX_DESC}
                  value={draft.otherInvolvedInstitutions}
                  readOnly={readOnly}
                  disabled={readOnly}
                  onChange={(e) =>
                    setDraft((d) =>
                      d
                        ? { ...d, otherInvolvedInstitutions: sliceMax(e.target.value, MAX_DESC) }
                        : d,
                    )
                  }
                />
                <FieldError message={errors.otherInvolvedInstitutions} />
              </div>
              <div className="ccr-fg">
                <label>
                  Name of the Legal Officer/ Officer In-Charge <span className="req">*</span>
                </label>
                <input
                  id="clit-legalOfficer"
                  className={inpClass(Boolean(errors.legalOfficerName))}
                  value={draft.legalOfficerName}
                  readOnly={readOnly}
                  disabled={readOnly}
                  maxLength={MAX_REF}
                  onChange={(e) => {
                    setDraft((d) =>
                      d ? { ...d, legalOfficerName: sliceMax(e.target.value, MAX_REF) } : d,
                    );
                    clearErr('legalOfficerName');
                  }}
                />
                <FieldError message={errors.legalOfficerName} />
              </div>
              <div className="ccr-fg">
                <label>
                  Contact Number <span className="req">*</span>
                </label>
                <input
                  id="clit-legalContact"
                  className={inpClass(Boolean(errors.legalOfficerContact))}
                  inputMode="numeric"
                  value={draft.legalOfficerContact}
                  readOnly={readOnly}
                  disabled={readOnly}
                  onChange={(e) => {
                    setDraft((d) =>
                      d
                        ? {
                            ...d,
                            legalOfficerContact: e.target.value
                              .replace(/\D/g, '')
                              .slice(0, MAX_CONTACT),
                          }
                        : d,
                    );
                    clearErr('legalOfficerContact');
                  }}
                />
                <FieldError message={errors.legalOfficerContact} />
              </div>
            </div>
          </ReviewSubBlock>

          <TaskCaseUpdateSectionActions
            canSave={canSaveStep2}
            saving={savingStep === 2}
            readOnly={readOnly}
            onSave={() => void saveSection(2)}
            onCancel={beginCancel}
          />
        </ReviewSection>

        <ReviewSection step="Step 03" title="Incident Details">
          <ReviewSubBlock title="Incident/ Matter Details">
            <div className="ccr-fg" style={{ marginBottom: 14 }}>
              <label>
                Subject of the Dispute <span className="req">*</span>
              </label>
              <textarea
                id="clit-subject"
                className={inpClass(Boolean(errors.subjectOfDispute))}
                rows={4}
                maxLength={MAX_TEXT}
                value={draft.subjectOfDispute}
                readOnly={readOnly}
                disabled={readOnly}
                onChange={(e) => {
                  setDraft((d) =>
                    d ? { ...d, subjectOfDispute: sliceMax(e.target.value, MAX_TEXT) } : d,
                  );
                  clearErr('subjectOfDispute');
                }}
              />
              <FieldError message={errors.subjectOfDispute} />
            </div>
            <div className="ccr-fr c2">
              <div className="ccr-fg">
                <label>Incident Occurred Date</label>
                <FormattedDateRangeInput
                  from={draft.incidentDateFrom}
                  to={draft.incidentDateTo}
                  enableYmdViews
                  disabled={readOnly}
                  onChange={(from, to) =>
                    setDraft((d) => (d ? { ...d, incidentDateFrom: from, incidentDateTo: to } : d))
                  }
                />
              </div>
              <div className="ccr-fg">
                <label>Next Step of the Case</label>
                <WizardSelect
                  value={draft.nextStepOfCaseId}
                  disabled={readOnly}
                  placeholder="Select Next Step"
                  options={toSelectOpts(lookups.nextSteps)}
                  onChange={(id) => {
                    const row = lookups.nextSteps.find((s) => s.id === id);
                    const name = row?.name ?? '';
                    const otherSelected = isOtherNextStep(name, row?.code ?? id);
                    setDraft((d) =>
                      d
                        ? {
                            ...d,
                            nextStepOfCaseId: id,
                            nextStepOfCaseName: name,
                            nextStepOfCaseOther: otherSelected ? d.nextStepOfCaseOther : '',
                          }
                        : d,
                    );
                    if (!otherSelected) clearErr('nextStepOfCaseOther');
                  }}
                />
              </div>
              <div className="ccr-fg">
                <label>Next Date of the Case</label>
                <FormattedDateInput
                  id="clit-nextCaseDate"
                  className={inpClass(Boolean(errors.nextCaseDate))}
                  value={draft.nextCaseDate}
                  minDate={minDeadlineDate}
                  enableYmdViews
                  disabled={readOnly}
                  onChange={(next) => {
                    setDraft((d) => (d ? { ...d, nextCaseDate: next } : d));
                    clearErr('nextCaseDate');
                  }}
                />
                <FieldError message={errors.nextCaseDate} />
              </div>
              {showNextStepOther ? (
                <div className="ccr-fg">
                  <label>Next Step (Other)</label>
                  <input
                    id="clit-nextStepOther"
                    className={inpClass(Boolean(errors.nextStepOfCaseOther))}
                    value={draft.nextStepOfCaseOther}
                    readOnly={readOnly}
                    disabled={readOnly}
                    maxLength={MAX_REF}
                    onChange={(e) => {
                      setDraft((d) =>
                        d ? { ...d, nextStepOfCaseOther: sliceMax(e.target.value, MAX_REF) } : d,
                      );
                      clearErr('nextStepOfCaseOther');
                    }}
                  />
                  <FieldError message={errors.nextStepOfCaseOther} />
                </div>
              ) : null}
            </div>
          </ReviewSubBlock>

          <ReviewSubBlock title="Timeline">
            <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
              <div className="ccr-fg">
                <label>
                  Urgency Level <span className="req">*</span>
                </label>
                <WizardSelect
                  id="clit-urgency"
                  hasError={Boolean(errors.urgencyLevelId)}
                  value={draft.urgencyLevelId}
                  disabled={readOnly}
                  placeholder="Select Urgency Level"
                  options={toSelectOpts(lookups.urgencyLevels)}
                  onChange={(id) => {
                    const name = lookups.urgencyLevels.find((u) => u.id === id)?.name ?? '';
                    setDraft((d) => (d ? { ...d, urgencyLevelId: id, urgencyLevelName: name } : d));
                    if (!isHighUrgencyLevel(name)) clearErr('urgencyJustification');
                    clearErr('urgencyLevelId');
                  }}
                />
                <FieldError message={errors.urgencyLevelId} />
              </div>
              <div className="ccr-fg">
                <label>Deadline (if any)</label>
                <FormattedDateInput
                  id="clit-deadline"
                  className={inpClass(Boolean(errors.deadline))}
                  value={draft.deadline}
                  minDate={minDeadlineDate}
                  enableYmdViews
                  disabled={readOnly}
                  onChange={(next) => {
                    setDraft((d) => (d ? { ...d, deadline: next } : d));
                    clearErr('deadline');
                  }}
                />
                <FieldError message={errors.deadline} />
              </div>
            </div>
            <div className="ccr-fg">
              <label>
                Urgency Justification{' '}
                {urgencyJustificationRequired ? <span className="req">*</span> : null}
              </label>
              <textarea
                id="clit-urgencyJustification"
                className={inpClass(Boolean(errors.urgencyJustification))}
                rows={3}
                maxLength={MAX_TEXT}
                value={draft.urgencyJustification}
                readOnly={readOnly}
                disabled={readOnly}
                onChange={(e) => {
                  setDraft((d) =>
                    d ? { ...d, urgencyJustification: sliceMax(e.target.value, MAX_TEXT) } : d,
                  );
                  clearErr('urgencyJustification');
                }}
              />
              <FieldError message={errors.urgencyJustification} />
            </div>
          </ReviewSubBlock>

          <TaskCaseUpdateSectionActions
            canSave={canSaveStep3}
            saving={savingStep === 3}
            readOnly={readOnly}
            onSave={() => void saveSection(3)}
            onCancel={beginCancel}
          />
        </ReviewSection>

        <ReviewSection step="Step 04" title="Submission & Physical File Delivery">
          <ReviewSubBlock title="Request Submitter's Details">
            <ReviewGrid cols={4}>
              <ReviewField label="Submitted Division/ Unit" value={submitter.division} />
              <ReviewField label="Designation" value={submitter.designation} />
              <ReviewField label="Submitted by" value={submitter.name} />
              <span className="ccr-review-grid-spacer" aria-hidden="true" />
              <ReviewField label="Email Address" value={submitter.email} />
              <ReviewField label="Contact Number" value={submitter.contact} />
            </ReviewGrid>
          </ReviewSubBlock>
          <ReviewSubBlock title="Physical File Delivery Method">
            <ReviewField label="Physical File Delivery Method" value={deliveryLabel} fullWidth />
            {draft.deliveryOther.trim() ? (
              <ReviewField label="Other Delivery Method" value={draft.deliveryOther} />
            ) : null}
          </ReviewSubBlock>
        </ReviewSection>
      </div>
    </>
  );
}
